import { capabilityDefinition } from "../capabilities/registry.js";
import { scopeMatches } from "./scope.js";
/**
 * أسبقية المنح — البلوبرنت §3.
 *
 * منع صريح ◀ منح صريح ◀ تفويض نشط ◀ منح الدور ◀ **رفض افتراضي**.
 *
 * الرقم الأعلى يفوز. والمنع الصريح يعلو كل شيء، لأنه استثناء مكتوب على شخص
 * بعينه بسبب مكتوب — ولا يجوز لمنحة دور أن تلغيه.
 */
const PRECEDENCE = { role: 1, delegation: 2, override: 3 };
function isActive(grant, at) {
    if (grant.startsAt !== undefined && at < grant.startsAt)
        return false;
    if (grant.expiresAt !== undefined && at >= grant.expiresAt)
        return false;
    return true;
}
export function snapshotCapabilities(actor, at) {
    const byCapability = new Map();
    for (const grant of actor.grants) {
        if (!isActive(grant, at))
            continue;
        const bucket = byCapability.get(grant.capability);
        if (bucket)
            bucket.push(grant);
        else
            byCapability.set(grant.capability, [grant]);
    }
    const resolved = new Map();
    for (const [capability, grants] of byCapability) {
        const top = Math.max(...grants.map((g) => PRECEDENCE[g.source]));
        const winners = grants.filter((g) => PRECEDENCE[g.source] === top);
        // منعٌ واحد في الطبقة الفائزة يُسقِط القدرة كلها — لا يُوازَن ولا يُجمَع
        if (winners.some((g) => g.effect === "revoke"))
            continue;
        resolved.set(capability, winners.map((g) => g.scope));
    }
    return {
        actor,
        at,
        held: new Set(resolved.keys()),
        scopesFor: (capability) => resolved.get(capability) ?? [],
    };
}
/**
 * هل يملك هذا الفاعل هذه القدرة على هذا السجل؟
 *
 * `record === null` لعملية لا تخصّ سجلاً بعينه — فتكفي الحيازة، ويضيّق مرشِّح
 * القائمة بعدها.
 */
export function can(snapshot, capability, record = null) {
    const scopes = snapshot.scopesFor(capability);
    if (scopes.length === 0)
        return false;
    return scopes.some((scope) => scopeMatches(scope, snapshot.actor, record));
}
/** هل يحمل القدرة بأي نطاق؟ للرسم في الواجهة فقط — **لا يصلح قراراً**. */
export function holds(snapshot, capability) {
    return snapshot.held.has(capability);
}
/**
 * سلطة مرتفعة: نطاق شركة أو قسم.
 *
 * تفرّق بين «أصل إليه لأنني مسؤول» و«أصل إليه لأنني طرف فيه» — وهو فرق حقيقي
 * في قواعد العمل: إعادة فتح منجَز بعد مهلة الساعة تحتاج الأولى لا الثانية.
 */
export function hasElevatedScope(snapshot, capability) {
    return snapshot
        .scopesFor(capability)
        .some((scope) => scope.type === "company" || scope.type === "department");
}
/** أنواع النطاق التي تُجيزها هذه القدرة في سجلّها — يُفرَض عند كل كتابة منحة. */
export function allowedScopesFor(capability) {
    return capabilityDefinition(capability).allowedScopes;
}
/**
 * لقطةٌ **للرسم وحده**، تُبنى من قائمة القدرات التي يعيدها السيرفر.
 *
 * الواجهة تحتاج أن تعرف ماذا تعرض في القائمة، ولا يجوز أن تعيد بناء محرّك
 * الصلاحيات لتعرف. فتأخذ الجواب من السيرفر وتمرّه عبر `holds` نفسها.
 *
 * **ولا نطاق فيها بقصد.** `scopesFor` ترجع فارغاً دائماً، فـ`can` عليها تقول
 * «لا» مهما كان السجل. أي محاولة لاتخاذ قرار صلاحية في المتصفّح تفشل هنا
 * بنيوياً — والقرار في السيرفر، حيث لا يستطيع أحد تعديله من أدوات المطوّر.
 */
export function renderSnapshot(userId, held) {
    return {
        actor: {
            userId,
            departmentIds: [],
            headedDepartmentIds: [],
            clientIds: [],
            managedClientIds: [],
            grants: [],
        },
        at: 0,
        held: new Set(held),
        scopesFor: () => [],
    };
}
//# sourceMappingURL=actor.js.map